import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CompanyMember {
  id: string;
  user_id: string;
  role: string;
  is_active: boolean | null;
  is_primary: boolean | null;
  joined_at: string | null;
  invited_at: string | null;
  profile?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

export interface CompanyAccount {
  id: string;
  name: string;
  legal_name: string | null;
  mc_number: string | null;
  dot_number: string | null;
  company_type: string;
  verification_status: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  created_at: string | null;
  documents_verified_at: string | null;
  documents_verified_by: string | null;
  account_phone?: string | null;
  account_email?: string | null;
}

export interface CreditTransaction {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  reference_key: string | null;
  created_at: string;
  user_id: string;
}

export interface SubscriptionInfo {
  plan: string;
  status: string;
  billing_cycle: string;
  monthly_price: number;
  seats_purchased: number;
  seats_used: number;
  trial_start_date: string | null;
  trial_end_date: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  cancel_at_period_end: boolean;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

export function useCompanyAccount() {
  const { user } = useAuth();
  const [company, setCompany] = useState<CompanyAccount | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = myRole === 'company_admin' || myRole === 'super_admin';

  // Resolve user's company membership
  useEffect(() => {
    if (!user) { setCompanyId(null); setLoading(false); return; }

    (async () => {
      const { data } = await supabase
        .from('company_users')
        .select('company_id, role')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      setCompanyId(data?.company_id ?? null);
      setMyRole(data?.role ?? null);
      if (!data) setLoading(false);
    })();
  }, [user]);

  // Fetch company details + members + wallet + transactions + subscription + invites
  const fetchAll = useCallback(async () => {
    if (!companyId) {
      setCompany(null);
      setMembers([]);
      setWalletBalance(0);
      setTransactions([]);
      setSubscription(null);
      setPendingInvites([]);
      return;
    }

    setLoading(true);

    const [companyRes, membersRes, walletRes, txRes, subRes, invitesRes] = await Promise.all([
      supabase
        .from('companies')
        .select('id, name, legal_name, mc_number, dot_number, company_type, verification_status, phone, email, website, address_line1, address_line2, city, state, postal_code, country, created_at, documents_verified_at, documents_verified_by')
        .eq('id', companyId)
        .maybeSingle(),
      supabase
        .from('company_users')
        .select('id, user_id, role, is_active, is_primary, joined_at, invited_at')
        .eq('company_id', companyId)
        .order('is_primary', { ascending: false }),
      supabase
        .from('company_credit_wallets')
        .select('balance')
        .eq('company_id', companyId)
        .maybeSingle(),
      supabase
        .from('credit_transactions')
        .select('id, type, amount, balance_after, description, reference_key, created_at, user_id')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('subscriptions')
        .select('plan, status, billing_cycle, monthly_price, seats_purchased, seats_used, trial_start_date, trial_end_date, current_period_start, current_period_end, stripe_customer_id, cancel_at_period_end')
        .eq('company_id', companyId)
        .in('status', ['active', 'trialing', 'past_due'])
        .maybeSingle(),
      supabase
        .from('company_invites' as any)
        .select('id, email, role, status, created_at, expires_at')
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false }),
    ]);

    const companyData = companyRes.data as CompanyAccount | null;
    let enrichedCompany: CompanyAccount | null = companyData;
    if (companyData && user) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('phone, email')
        .eq('id', user.id)
        .maybeSingle();
      enrichedCompany = {
        ...companyData,
        account_phone: profileData?.phone ?? user.phone ?? null,
        account_email: profileData?.email ?? user.email ?? null,
      };
    }
    setCompany(enrichedCompany);
    setSubscription(subRes.data as SubscriptionInfo | null);
    setPendingInvites((invitesRes.data || []) as unknown as PendingInvite[]);

    // Fetch profile info for each member
    const rawMembers = (membersRes.data || []) as any[];
    if (rawMembers.length > 0) {
      const userIds = rawMembers.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url')
        .in('id', userIds);

      const profileMap = new Map((profiles || []).map(p => [p.id, p]));
      const enriched = rawMembers.map(m => ({
        ...m,
        profile: profileMap.get(m.user_id) || null,
      }));
      setMembers(enriched);
    } else {
      setMembers([]);
    }

    setWalletBalance(walletRes.data?.balance ?? 0);
    setTransactions((txRes.data || []) as CreditTransaction[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const updateCompany = useCallback(async (updates: Partial<CompanyAccount>) => {
    if (!companyId) return { error: 'No company' };
    const { error } = await supabase
      .from('companies')
      .update(updates as any)
      .eq('id', companyId);
    if (!error) {
      setCompany(prev => prev ? { ...prev, ...updates } : prev);
    }
    return { error };
  }, [companyId]);

  const updateMemberRole = useCallback(async (membershipId: string, newRole: string) => {
    if (!companyId) return { error: 'No company' };
    const { error } = await supabase
      .from('company_users')
      .update({ role: newRole as any })
      .eq('id', membershipId)
      .eq('company_id', companyId);
    if (!error) {
      setMembers(prev => prev.map(m => m.id === membershipId ? { ...m, role: newRole } : m));
    }
    return { error };
  }, [companyId]);

  return {
    company,
    members,
    myRole,
    isAdmin,
    walletBalance,
    transactions,
    subscription,
    pendingInvites,
    companyId,
    loading,
    updateCompany,
    updateMemberRole,
    refresh: fetchAll,
  };
}
